/**
 * OCB — Personnalisation de produit (option gratuite ou payante)
 *
 * Chargé SANS defer dans le <head> : le listener capture de soumission doit
 * être enregistré AVANT les handlers inline du thème (buy-buttons) pour
 * pouvoir intercepter l'ajout panier quand une option payante est active.
 *
 * Trois responsabilités :
 *
 * 1. <ocb-personalization> (fiche produit) : case à cocher de l'option payante,
 *    champ de saisie -> properties[...] (inputs liés au form principal via
 *    l'attribut form=), prix mis à jour sur la fiche quand l'option est cochée,
 *    masquage des boutons de checkout dynamique (Shop Pay/PayPal) qui
 *    court-circuiteraient l'ajout du supplément.
 *
 * 2. Interception de la soumission : quand une option payante est active,
 *    ajoute en UNE requête /cart/add.js le produit principal + le produit
 *    supplément, liés par les propriétés internes _for/_perso_fee (préfixe _
 *    = masqué du panier/checkout côté client). Gère aussi le sticky ATC qui a
 *    son propre <form> (SMT) en y greffant les propriétés saisies.
 *
 * 3. Synchronisation panier (toutes pages) : la quantité de chaque ligne
 *    « supplément » doit toujours égaler la somme des quantités des lignes
 *    principales qui la référencent (properties._perso_fee). Suppression du
 *    principal -> suppression du supplément. Détection via un wrapper
 *    fetch/XHR sur /cart/add|change|update|clear.
 */
(function () {
  'use strict';

  if (window.OCBPersonalization) return;
  window.OCBPersonalization = true;

  function routes() { return window.routes || {}; }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function sectionsToRender() {
    var cart = cartElement();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      try {
        return cart.getSectionsToRender().map(function (s) { return s.id || s.section; });
      } catch (e) { /* fall through */ }
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  /**
   * Replace the rendered cart sections in place without closing the drawer.
   * Tries the containers the themes actually re-render (.js-contents on
   * Vanguard, #CartDrawer on SMT), falls back to the section wrapper.
   */
  function renderCartSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (name) {
      var html = sections[name];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var wrapper = document.getElementById('shopify-section-' + name);
      var swapped = ['.js-contents', '#CartDrawer'].some(function (selector) {
        var oldEl = (wrapper || document).querySelector(selector);
        var newEl = doc.querySelector(selector);
        if (oldEl && newEl) {
          oldEl.replaceWith(newEl);
          return true;
        }
        return false;
      });
      if (!swapped && wrapper) {
        wrapper.innerHTML = doc.body.firstElementChild ? doc.body.firstElementChild.innerHTML : html;
      }
    });
    document.dispatchEvent(new CustomEvent('ocb:personalization:cart-rendered'));
  }

  /* ------------------------------------------------------------------ */
  /* <ocb-personalization>                                               */
  /* ------------------------------------------------------------------ */

  var STYLE_ID = 'ocb-personalization-css';
  function injectGlobalCss() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = '.ocb-perso-hide-express .shopify-payment-button{display:none !important}';
    document.head.appendChild(style);
  }

  function allBlocks() {
    return Array.prototype.slice.call(document.querySelectorAll('ocb-personalization'));
  }

  /**
   * Enabled personalization blocks attached to this form. Matches by form id
   * (inputs associated via form=) or, for secondary forms of the same section
   * (sticky ATC which has its own <form> without data-type), by section id.
   */
  function blocksForForm(form) {
    if (!form) return [];
    var sectionWrapper = form.closest('.shopify-section');
    var sectionId = sectionWrapper && sectionWrapper.id.replace(/^shopify-section-/, '');
    return allBlocks().filter(function (el) {
      if (typeof el.isEnabled !== 'function' || !el.isEnabled()) return false;
      if (form.id && el.dataset.formId === form.id) return true;
      return !!(sectionId && el.dataset.sectionId === sectionId);
    });
  }

  function paidBlocks(blocks) {
    return blocks.filter(function (el) { return !!el.dataset.feeVariantId; });
  }

  var proto = Object.create(HTMLElement.prototype);

  proto.connectedCallback = function () {
    // Ce script est chargé SANS defer : le parseur appelle connectedCallback
    // à l'ouverture du tag, AVANT que les enfants existent. Différer l'init.
    var self = this;
    if (document.readyState === 'loading' && !this.querySelector('.product-personalization__input')) {
      document.addEventListener('DOMContentLoaded', function () { self.init(); });
      return;
    }
    this.init();
  };

  proto.init = function () {
    if (this.ocbInitialized) return;
    this.ocbInitialized = true;
    injectGlobalCss();
    var self = this;
    this.checkbox = this.querySelector('.product-personalization__checkbox');
    this.field = this.querySelector('.product-personalization__field');
    this.input = this.querySelector('.product-personalization__input');
    this.feeFlag = this.querySelector('.product-personalization__fee-flag');
    this.count = this.querySelector('.product-personalization__count');

    if (this.checkbox) {
      this.checkbox.addEventListener('change', function () { self.onToggle(); });
    }
    if (this.input) {
      this.input.addEventListener('input', function () {
        self.updateCount();
        self.classList.remove('product-personalization--invalid');
      });
      this.updateCount();
    }
    this.syncExpressButtons();
    if (this.isPaid() && this.isEnabled()) this.applyPrice(true);

    // Re-apply the price add-on after the theme re-renders it on variant change.
    if (this.isPaid()) {
      document.addEventListener('change', function () {
        setTimeout(function () { self.applyPrice(self.isEnabled()); }, 500);
      });
    }
  };

  proto.isPaid = function () {
    return !!this.dataset.feeVariantId;
  };

  proto.isEnabled = function () {
    if (!this.isPaid()) return true;
    if (this.dataset.alwaysOn === 'true') return true;
    return !!(this.checkbox && this.checkbox.checked);
  };

  proto.onToggle = function () {
    var enabled = this.isEnabled();
    if (this.field) this.field.hidden = !enabled;
    if (this.feeFlag) this.feeFlag.disabled = !enabled;
    if (this.input) {
      this.input.required = enabled && this.isPaid();
      if (!enabled) this.classList.remove('product-personalization--invalid');
    }
    this.syncExpressButtons();
    this.applyPrice(enabled);
  };

  proto.updateCount = function () {
    if (!this.count || !this.input) return;
    var tpl = this.count.dataset.countTemplate || '[n]';
    this.count.textContent = tpl.replace('[n]', String(this.input.value.length));
  };

  /** Hide dynamic checkout buttons while any paid option of this form is on. */
  proto.syncExpressButtons = function () {
    var form = document.getElementById(this.dataset.formId);
    if (!form) return;
    var anyPaid = paidBlocks(blocksForForm(form)).length > 0;
    var wrapper = form.closest('product-form') || form;
    wrapper.classList.toggle('ocb-perso-hide-express', anyPaid);
    // The sticky ATC (SMT) has its own express buttons.
    var sticky = document.getElementById('sticky-atc-form-' + this.dataset.sectionId);
    if (sticky) {
      var stickyWrapper = sticky.closest('product-form') || sticky;
      stickyWrapper.classList.toggle('ocb-perso-hide-express', anyPaid);
    }
  };

  /**
   * Update the displayed product price with the supplement included.
   * data-price-map = {"<variantId>": "12,90 €", ...} rendered in Liquid
   * (variant price + fee price, already money-formatted).
   */
  proto.applyPrice = function (enabled) {
    var mapJson = this.dataset.priceMap;
    if (!mapJson) return;
    var map;
    try { map = JSON.parse(mapJson); } catch (e) { return; }

    var form = document.getElementById(this.dataset.formId);
    var idInput = form && form.querySelector('[name="id"]');
    var variantId = idInput && idInput.value;
    if (!variantId || !map[variantId]) return;

    var selectors = (this.dataset.priceSelectors || '').split('|').filter(Boolean);
    var sectionEl = this.closest('.shopify-section') || document;
    for (var i = 0; i < selectors.length; i++) {
      var el = sectionEl.querySelector(selectors[i]);
      // Les thèmes rendent le prix "solde" ET le prix normal, l'un des deux
      // masqué en CSS : ne toucher que l'élément réellement visible.
      if (!el || el.offsetParent === null) continue;
      if (enabled) {
        if (el.dataset.ocbOriginalPrice === undefined) el.dataset.ocbOriginalPrice = el.textContent;
        el.textContent = map[variantId];
      } else if (el.dataset.ocbOriginalPrice !== undefined) {
        el.textContent = el.dataset.ocbOriginalPrice;
        delete el.dataset.ocbOriginalPrice;
      }
      break;
    }
  };

  if (!customElements.get('ocb-personalization')) {
    var El = function () { return Reflect.construct(HTMLElement, [], El); };
    El.prototype = proto;
    Object.setPrototypeOf(El, HTMLElement);
    customElements.define('ocb-personalization', El);
  }

  /* ------------------------------------------------------------------ */
  /* Submit interception                                                 */
  /* ------------------------------------------------------------------ */

  function formVariantId(form) {
    var fd = new FormData(form);
    var id = fd.get('id');
    if (!id) {
      // SMT keeps [name=id] disabled until its JS runs; disabled inputs are
      // excluded from FormData.
      var input = form.querySelector('[name="id"]');
      id = input && input.value;
    }
    return parseInt(id, 10);
  }

  function collectMainItem(form, blocks) {
    var fd = new FormData(form);
    var item = {
      id: formVariantId(form),
      quantity: parseInt(fd.get('quantity'), 10) || 1,
      properties: {}
    };
    if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
    fd.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== '') item.properties[m[1]] = value;
    });
    // Sticky form (SMT): the personalization inputs belong to the MAIN form,
    // graft their values in.
    blocks.forEach(function (b) {
      var title = b.dataset.optionTitle;
      if (b.input && b.input.value.trim() !== '' && title && !item.properties[title]) {
        item.properties[title] = b.input.value;
      }
      if (b.dataset.feeVariantId) item.properties._perso_fee = b.dataset.feeVariantId;
    });
    return item;
  }

  function showFormError(form, message) {
    var wrapper = form.closest('product-form');
    var errEl = wrapper && wrapper.querySelector('.product-form__error-message');
    if (errEl) {
      errEl.textContent = message;
      var errWrap = errEl.closest('.product-form__error-message-wrapper');
      if (errWrap) errWrap.removeAttribute('hidden');
    } else {
      window.alert(message);
    }
  }

  function isAddToCartForm(form) {
    if (form.getAttribute('data-type') === 'add-to-cart-form') return true;
    var action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') !== -1;
  }

  function onSubmitCapture(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM' || !isAddToCartForm(form)) return;

    var blocks = blocksForForm(form);
    if (!blocks.length) return;

    // Secondary form of the section (sticky ATC): the personalization inputs
    // are NOT associated to it, its native flow would lose them.
    var mainBlock = blocks[0];
    var isSecondary = !form.id || form.id !== mainBlock.dataset.formId;
    var paid = paidBlocks(blocks);

    // Main form + free options only: the native flow already carries the
    // properties[...] inputs (associated via form=). Nothing to do.
    if (!paid.length && !isSecondary) return;

    // Required check. The browser can't validate form=-associated inputs of
    // ANOTHER form (sticky) and programmatic submits skip validation.
    var invalid = blocks.filter(function (b) {
      return b.input && b.input.required && b.input.value.trim() === '';
    });
    if (invalid.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      invalid.forEach(function (b) { b.classList.add('product-personalization--invalid'); });
      invalid[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (invalid[0].input) invalid[0].input.focus({ preventScroll: true });
      return;
    }

    // From here: paid option active, or sticky form (whose native flow would
    // lose the free personalization text). Take over the add.
    event.preventDefault();
    event.stopImmediatePropagation();

    var mainItem = collectMainItem(form, blocks);
    // Supplements FIRST: Shopify shows the most recently added line on top,
    // so the main product ends up above its supplement(s) in the cart.
    var items = paid.map(function (b) {
      return {
        id: parseInt(b.dataset.feeVariantId, 10),
        quantity: mainItem.quantity,
        properties: {
          _for: String(mainItem.id),
          'Pour': b.dataset.productTitle || '',
          'Option': b.dataset.optionTitle || '',
          'Texte': (b.input && b.input.value) || ''
        }
      };
    });
    items.push(mainItem);

    var submitBtn = form.querySelector('[type="submit"], button[name="add"]');
    if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');

    var addUrl = (routes().cart_add_url || '/cart/add') + '.js';
    fetch(addUrl.replace(/\.js\.js$/, '.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: items,
        sections: sectionsToRender(),
        sections_url: window.location.pathname
      })
    })
      .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
      .then(function (result) {
        if (!result.ok) {
          showFormError(form, (result.json && (result.json.description || result.json.message)) || 'Erreur lors de l’ajout au panier.');
          return;
        }
        var cart = cartElement();
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(result.json);
        } else if (result.json.sections) {
          renderCartSections(result.json.sections);
        } else {
          window.location.href = routes().cart_url || '/cart';
        }
      })
      .catch(function () {
        window.location.href = routes().cart_url || '/cart';
      })
      .finally(function () {
        if (submitBtn) submitBtn.removeAttribute('disabled');
      });
  }

  document.addEventListener('submit', onSubmitCapture, true);

  /* ------------------------------------------------------------------ */
  /* Cart sync (all pages)                                               */
  /* ------------------------------------------------------------------ */

  var syncScheduled = false;
  var syncing = false;

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;
    setTimeout(function () {
      syncScheduled = false;
      runSync();
    }, 300);
  }

  function runSync() {
    if (syncing) return;
    var cartJsUrl = (routes().cart_url || '/cart') + '.js';
    fetch(cartJsUrl, { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // A supplement line must equal the total quantity of the main lines
        // that reference it: same fee variant (_perso_fee), same main variant
        // (_for) and same personalized text (the main line stores it under
        // the option title, carried on the supplement as Option/Texte).
        var mains = cart.items.filter(function (i) {
          return i.properties && i.properties._perso_fee;
        });
        var updates = {};
        cart.items.forEach(function (item) {
          var forId = item.properties && item.properties._for;
          if (!forId) return; // not a supplement line
          var optName = item.properties['Option'];
          var text = item.properties['Texte'] || '';
          var want = 0;
          mains.forEach(function (m) {
            if (String(m.properties._perso_fee) !== String(item.variant_id)) return;
            if (String(m.variant_id) !== String(forId)) return;
            if (optName && (m.properties[optName] || '') !== text) return;
            want += m.quantity;
          });
          if (want !== item.quantity) updates[item.key] = want;
        });

        if (!Object.keys(updates).length) return;

        syncing = true;
        var updateUrl = (routes().cart_update_url || '/cart/update') + '.js';
        return fetch(updateUrl.replace(/\.js\.js$/, '.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            updates: updates,
            sections: sectionsToRender().join(',')
          })
        })
          .then(function (res) { return res.json(); })
          .then(function (json) {
            renderCartSections(json.sections);
          })
          .finally(function () { syncing = false; });
      })
      .catch(function () { syncing = false; });
  }

  /* Watch every cart mutation done by the theme (fetch + XHR wrappers). */
  var origFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    var promise = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      promise.then(function (res) {
        if (res.ok && !syncing) scheduleSync();
      }).catch(function () {});
    }
    return promise;
  };

  var origOpen = window.XMLHttpRequest.prototype.open;
  window.XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300 && !syncing) scheduleSync();
      });
    }
    return origOpen.apply(this, arguments);
  };
})();
